import * as React from 'react';
import { connect } from 'react-redux';
import MatchList, { MatchListAddMoreAnchor } from '../../match-list';
import { AppState } from '@om-fe/core/core/types';
import { areParticipantMatchListTopicPropsEqual, getParticipantMatchListSliceName } from './participant-match-list.utils';
import {
    getParticipantMatchList,
    removeParticipantMatchList,
    updateParticipantMatchList,
} from './participant-match-list.actions';
import {
    ParticipantMatchListStoreModel,
} from './participant-match-list.constants';
import { compose } from 'redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withRouting from '@om-fe/core/core/routing/with-routing';
import { EMRoutingContextInterface } from '@om-fe/core/core/routing/utils';
import { MatchListCommon } from '@om-fe/core/components/match-list/utils/match-list-models';
import { MatchListItemLayout } from '../../utils/match-list.constants';
import MatchListSkeleton from '@om-fe/core/components/match-list/match-list-skeleton';
import { DisciplineLiveStatus } from '@om-fe/models';
import { MarketsHeaderOptions } from '@om-fe/core/utils/config';
import { PlatformTypes } from '@om-fe/utils';

export interface ParticipantMatchListPublicProps extends MatchListCommon {
    readonly instanceId: string;
    readonly participantId: string;
    readonly redirectAnchor?: MatchListAddMoreAnchor;
    readonly itemLayout?: MatchListItemLayout;
    readonly limitNumberOfEvents: number | undefined;
    readonly liveStatus: DisciplineLiveStatus;
    readonly mainMarketsLimit?: number;
    readonly marketsHeaderOptions: MarketsHeaderOptions;
    readonly showMatchListHeader: boolean;
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly deviceType?: PlatformTypes;
}

interface ParticipantMatchListStoreProps {
    readonly sliceKey: string;
    readonly limit: number;
    readonly totalRecords: number;
    readonly ready: boolean;
}

interface ParticipantMatchListDispatchProps {
    readonly addMore: (instanceId: string, limitNumberOfEvents: number) => void;
}

type ParticipantMatchListProps = ParticipantMatchListPublicProps
    & ParticipantMatchListStoreProps
    & I18nContextInterface
    & EMRoutingContextInterface;

class ParticipantMatchListComponent extends React.Component<ParticipantMatchListProps> {
    render(): React.ReactNode {
        const {
            instanceId,
            sliceKey,
            disciplineId,
            limit,
            limitNumberOfEvents,
            addMore,
            redirectAnchor,
            marketsHeaderOptions,
            showMixedMatchListHeader,
            itemLayout,
            mainMarketsLimit,
            showMatchListHeader,
            showMarketInMatchListGroupHeader,
            deviceType,
        } = this.props;

        return (
            <MatchList
                itemLayout={itemLayout}
                addMore={redirectAnchor ? undefined : addMore}
                addMoreAnchor={redirectAnchor}
                disciplineId={disciplineId}
                instanceId={instanceId}
                limit={limit}
                limitNumberOfEvents={limitNumberOfEvents}
                liveStatus={DisciplineLiveStatus.BOTH}
                marketsHeaderOptions={marketsHeaderOptions}
                showMixedMatchListHeader={showMixedMatchListHeader}
                sliceKey={sliceKey}
                totalRecords={undefined}
                mainMarketsLimit={mainMarketsLimit}
                showMatchListHeader={showMatchListHeader}
                showMarketInMatchListGroupHeader={showMarketInMatchListGroupHeader}
                deviceType={deviceType}
            />
        );
    }
}

const ParticipantMatchList: React.ComponentType<ParticipantMatchListPublicProps> = compose<React.ComponentType<ParticipantMatchListPublicProps>>(
    withRouting(),
    withTranslation<ParticipantMatchListPublicProps>(),
    preLoader<ParticipantMatchListPublicProps>({
        preload({ props, dispatch }) {
            const { instanceId, participantId, mainMarketsLimit, liveStatus} = props;

            dispatch(getParticipantMatchList(instanceId, participantId, liveStatus, mainMarketsLimit));
            
        },
        getReadySlice({ instanceId }: ParticipantMatchListPublicProps): string {
            const slice: string = getParticipantMatchListSliceName(instanceId);

            return `${slice}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            const { instanceId, participantId, mainMarketsLimit, liveStatus } = props;

            if (instanceId !== prevProps.instanceId) {
                dispatch(removeParticipantMatchList(prevProps.instanceId));
                dispatch(getParticipantMatchList(instanceId, participantId, liveStatus, mainMarketsLimit));
            } else if (!areParticipantMatchListTopicPropsEqual(prevProps, props)) {
                dispatch(updateParticipantMatchList(instanceId, participantId, liveStatus, mainMarketsLimit));
            }
        },
        destroy({ props, dispatch }) {
            const { instanceId } = props;

            dispatch(removeParticipantMatchList(instanceId));
        },
        loadingAnimation: MatchListSkeleton,
    }),
    connect<ParticipantMatchListStoreProps,
        ParticipantMatchListDispatchProps,
        ParticipantMatchListPublicProps>(
        (state: AppState, props: ParticipantMatchListPublicProps): ParticipantMatchListStoreProps => {
            const { instanceId } = props;
            const sliceKey: string = getParticipantMatchListSliceName(instanceId);

            const slice: ParticipantMatchListStoreModel | undefined = state[sliceKey];

            if (!slice) {
                return {
                    sliceKey,
                    limit: 0,
                    totalRecords: 0,
                    ready: false,
                };
            }

            return {
                sliceKey,
                limit: slice.limit,
                totalRecords: slice.totalRecords,
                ready: slice.ready,
            };
        }
    ),
)(ParticipantMatchListComponent);
export default ParticipantMatchList;
